import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HeroIcons } from '@/components/design-system/atoms/icon';

interface ImageUploadProps {
  onImageUpload: (file: File | null) => void;
  onRemoveImage?: () => void;
  label?: string;
  className?: string;
  imageUrl?: string;
  maxSize?: number;
  acceptedFileTypes?: string[];
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageUpload,
  onRemoveImage,
  label,
  className = '',
  imageUrl,
  maxSize = 5242880, // 5MB
  acceptedFileTypes = ['image/png', 'image/svg', 'image/jpg', 'image/jpeg'],
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (imageUrl && !previewUrl) {
      setPreviewUrl(imageUrl);
    }
  }, [imageUrl, previewUrl]);

  const validateFile = (file: File): boolean => {
    setError(null);

    // Check file size
    if (file.size > maxSize) {
      setError(t('js.features.admin.broadcast_messages.form.file_too_large'));
      return false;
    }

    // Check file type
    if (!acceptedFileTypes.includes(file.type)) {
      setError(t('js.features.admin.broadcast_messages.form.invalid_file_type'));
      return false;
    }

    return true;
  };

  const handleFile = (file: File) => {
    if (validateFile(file)) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      onImageUpload(file);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewUrl(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onImageUpload(null);
    onRemoveImage?.();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label
          htmlFor="image-upload"
          className="w-full break-words text-footnote1 capitalize leading-[16px] text-N700 text-text-secondary"
        >
          {label}
        </label>
      )}
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`flex h-[135px] w-[160px] cursor-pointer flex-col items-center justify-center rounded-lg border-[1.5px] border-dashed border-[#1B998B] bg-bg-successLight p-3 hover:border-gray-400 ${
          isDragging ? 'border-primary-500' : ''
        } ${className}`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick();
          }
        }}
        aria-label={t('js.features.admin.broadcast_messages.form.image').toString()}
      >
        <input
          ref={inputRef}
          id="image-upload"
          type="file"
          className="hidden"
          accept={acceptedFileTypes.join(',')}
          onChange={handleChange}
          aria-label={t('js.features.admin.broadcast_messages.form.image_placeholder').toString()}
        />

        {previewUrl ? (
          <div className="relative h-full w-full">
            <img
              src={previewUrl}
              alt={t('js.features.admin.broadcast_messages.form.preview').toString()}
              className="h-full w-full object-contain"
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute left-[50%] top-[50%] flex h-[38px] w-[42px] items-center justify-center rounded-full bg-white px-[12px] py-2 shadow-md hover:bg-gray-100"
              style={{ transform: 'translate(-50%, -50%)' }}
              aria-label={t('js.features.admin.broadcast_messages.form.remove_image').toString()}
            >
              <HeroIcons name="trash" size={18} className="text-text-primaryHeavy" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center">
            <HeroIcons name="image" size={32} className="text-[#1B998B]" />
            <p className="text-body text-text-secondary">
              {t('js.features.admin.broadcast_messages.form.image_placeholder')}
            </p>
          </div>
        )}
        {error && <p className="mt-1 text-sm text-text-dangerHeavy">{error}</p>}
      </div>

      <div className="flex max-w-[160px] flex-col gap-2">
        <p className="text-footnote2 text-text-primaryLight">
          {t('js.features.admin.broadcast_messages.form.limit_image_placeholder')}
        </p>
      </div>
    </div>
  );
};
